import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { linesApi, inspectionsApi } from '../services/api';

export function LineDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [line, setLine] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    linesApi.get(id)
      .then(setLine)
      .catch((err) => setError(err.detail || 'Failed to load line'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (error || !line) {
    return (
      <div className="page-body">
        <div className="card text-center" style={{ padding: 48 }}>
          <h3 style={{ color: 'var(--status-fail)' }}>Error</h3>
          <p className="text-muted">{error || 'Line not found'}</p>
          <button className="btn btn-secondary mt-4" onClick={() => navigate('/lines')}>Back to Lines</button>
        </div>
      </div>
    );
  }

  const stats = line.stats || {};
  const defects = line.defectDistribution || [];
  const yieldTrend = line.yieldTrend || [];
  const devices = line.devices || [];
  const goldenSamples = line.goldenSamples || [];

  return (
    <>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/lines')}>← Back</button>
              <h1>{line.name}</h1>
              <span className={`badge ${
                line.status === 'active' ? 'badge-pass' :
                line.status === 'paused' ? 'badge-warning' : 'badge-neutral'
              }`}>
                {line.status}
              </span>
            </div>
            <p className="font-mono">{line.code} · {formatProcess(line.packagingProcess)} · {line.targetSpeedPpm} ppm target</p>
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Inspections</div>
            <div className="stat-value">{(stats.totalInspections || 0).toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pass Rate</div>
            <div className={`stat-value ${(stats.passRate || 0) >= 99 ? 'pass' : (stats.passRate || 0) >= 95 ? 'warning' : 'fail'}`}>
              {(stats.passRate || 0).toFixed(2)}%
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Failures</div>
            <div className="stat-value fail">{(stats.failCount || 0).toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Avg Confidence</div>
            <div className="stat-value info">
              {stats.avgConfidence ? `${(stats.avgConfidence * 100).toFixed(0)}%` : '—'}
            </div>
          </div>
        </div>

        <div className="grid-2 mb-6">
          {/* Defect Distribution */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Defect Distribution</div>
            </div>
            {defects.length > 0 ? (
              <div>
                {defects.map((d: any) => (
                  <div key={d.defectType} style={{ marginBottom: 12 }}>
                    <div className="flex justify-between text-sm" style={{ marginBottom: 4 }}>
                      <span>{formatDefectType(d.defectType)}</span>
                      <span className="font-mono text-muted">{d.count} ({d.percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{
                        width: `${d.percentage}%`,
                        background: d.defectType === 'contamination' ? 'var(--status-fail)' : 'var(--accent-gradient)',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '24px 0' }}>
                <p className="text-muted text-sm">No defects recorded yet</p>
              </div>
            )}
          </div>

          {/* Yield Trend */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Yield Trend (24h)</div>
            </div>
            {yieldTrend.length > 0 ? (
              <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 2, padding: '0 4px' }}>
                {yieldTrend.map((point: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: `${point.value}%`,
                      minHeight: 2,
                      background: point.value >= 99 ? 'var(--status-pass)' :
                                  point.value >= 95 ? 'var(--status-warning)' : 'var(--status-fail)',
                      borderRadius: '2px 2px 0 0',
                      opacity: 0.8,
                    }}
                    title={`${new Date(point.timestamp).toLocaleTimeString()}: ${point.value.toFixed(1)}%`}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '24px 0' }}>
                <p className="text-muted text-sm">No data available yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Devices */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="card-title">Edge Devices</div>
            <span className="text-sm text-muted">{devices.length} device{devices.length !== 1 ? 's' : ''}</span>
          </div>
          {devices.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Status</th>
                    <th>Model</th>
                    <th>Last Heartbeat</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device: any) => (
                    <tr key={device.id}>
                      <td style={{ fontWeight: 500 }}>{device.name}</td>
                      <td>
                        <span className="flex items-center gap-2">
                          <span className={`status-dot ${device.status === 'online' ? 'online' : device.status === 'error' ? 'error' : 'offline'}`} />
                          {device.status}
                        </span>
                      </td>
                      <td className="font-mono text-sm">{device.model}</td>
                      <td className="text-sm text-muted">
                        {device.lastHeartbeatAt ? new Date(device.lastHeartbeatAt).toLocaleString() : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <p className="text-muted text-sm">No devices registered for this line</p>
            </div>
          )}
        </div>

        {/* Golden Samples */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Golden Samples (Calibration Baselines)</div>
          </div>
          {goldenSamples.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Samples</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {goldenSamples.map((sample: any) => (
                    <tr key={sample.id}>
                      <td style={{ fontWeight: 500 }}>{sample.name}</td>
                      <td>
                        <span className={`badge ${
                          sample.status === 'active' ? 'badge-pass' :
                          sample.status === 'collecting' ? 'badge-info' : 'badge-neutral'
                        }`}>
                          {sample.status}
                        </span>
                      </td>
                      <td className="font-mono">{sample.sampleCount}</td>
                      <td className="text-sm text-muted">{new Date(sample.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <p className="text-muted text-sm">No golden samples calibrated yet. Present 30–50 known-good seals to establish a baseline.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function formatProcess(process: string): string {
  const labels: Record<string, string> = {
    thermoforming: 'Thermoforming', vffs: 'VFFS', hffs: 'HFFS',
    tray_sealing: 'Tray Sealing', blister: 'Blister',
  };
  return labels[process] || process;
}

function formatDefectType(type: string): string {
  const labels: Record<string, string> = {
    contamination: 'Contamination', wrinkle: 'Wrinkle / Fold',
    under_heating: 'Under-Heating', over_heating: 'Over-Heating',
    incomplete_seal: 'Incomplete Seal', misalignment: 'Misalignment', other: 'Other',
  };
  return labels[type] || type;
}
